import { sourceAddressSchema } from '@getcaya/schemas'

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { AddressForm, withSourceForm } from './AddressForm'
import type { AddressFormProps } from './AddressForm/Form'
import type { RequiredProps, WithSourceFormProps } from './AddressForm/withForm'

interface Props extends Omit<RequiredProps, 'translatedStrings'> {
  onCancel?: () => any
}

type WrappedProps = Props & Pick<RequiredProps, 'translatedStrings'>

const AddressFormWithTranslatedLabels = ({
  isSubmitting,
  translatedStrings: _formTranslatedStrings,
  ...formikProps
}: WithSourceFormProps<WrappedProps>) => {
  const { t } = useTranslation()
  const innerStrings = useMemo<AddressFormProps['translatedStrings']>(
    () => ({
      addName: t('content.addressForm.addName'),
      addNameDescription: t('content.addressForm.addNameDescription', {
        num: formikProps.formConfig.maxNames,
      }),
      business: t('content.sourceForm.business'),
      cancel: t('labels.cancel'),
      city: t('content.addressForm.city'),
      cityPlaceholder: t('content.addressForm.cityPlaceholder'),
      country: t('content.addressForm.country'),
      countryPlaceholder: t('content.addressForm.countryPlaceholder'),
      nameForSources: t('content.addressForm.nameForSources'),
      namePlaceholderForSources: t(
        'content.addressForm.namePlaceholderForSources'
      ),
      nCharsField: num => t('content.addressForm.validation.nChars', { num }),
      private: t('content.sourceForm.private'),
      requiredField: t('content.addressForm.validation.required'),
      street: t('content.addressForm.street'),
      streetPlaceholder: t('content.addressForm.streetPlaceholder'),
      submit: t('labels.save'),
      zip: t('content.addressForm.zip'),
      zipPlaceholder: t('content.addressForm.zipPlaceholder'),
      // TODO: add proper translation key
      nameForBusinessSources: '',
      // TODO: add proper translation key
      namePlaceholderForBusinessSources: '',
    }),
    [t, formikProps.formConfig.maxNames]
  )

  return (
    <AddressForm
      {...formikProps}
      isSubmitting={isSubmitting}
      translatedStrings={innerStrings}
      loading={isSubmitting}
    />
  )
}

const EnhancedAddressForm = withSourceForm<WrappedProps>({
  validationSchema: ({ translatedStrings, formConfig }: RequiredProps) =>
    toFormikValidationSchema(
      sourceAddressSchema({
        translatedStrings,
        ...formConfig,
      })
    ),
})(AddressFormWithTranslatedLabels)

const Form = (props: Props) => {
  const { t } = useTranslation()
  const formStrings = useMemo<RequiredProps['translatedStrings']>(
    () => ({
      submitFailed: t('content.sourceForm.submitFailed'),
      nCharsField: num => t('content.addressForm.validation.nChars', { num }),
      requiredField: t('content.addressForm.validation.required'),
      maxCharsN: num => t('content.addressForm.validation.maxCharsN', { num }),
      maxNamesN: num => t('content.addressForm.validation.maxNamesN', { num }),
    }),
    [t]
  )
  return <EnhancedAddressForm {...props} translatedStrings={formStrings} />
}

export default Form
